import { createCanvas, destroyCanvas, drawImage, isCanvasImageSource } from "@/libs/canvas"
import { type GraphInput } from "@/libs/cv"
import type { ImageSource } from "@/libs/image"
import type { FaceDetection } from "../../modules/types"
import { create as createQualityOnlineGraph, type QualityMetrics } from "../quality/quality-online"

export interface BestFrameResult {
  image: ImageSource
  quality: QualityMetrics
}

export class BestFrameSelector {
  private _imageInput: GraphInput<ImageSource>
  private _faceInput: GraphInput<FaceDetection | null>
  private _quality: AsyncIterator<QualityMetrics, QualityMetrics, QualityMetrics>
  private _running: boolean = false
  private _bestFrame: BestFrameResult | null = null
  private _frameCount: number = 0

  constructor(
    imageInput: GraphInput<ImageSource>,
    faceInput: GraphInput<FaceDetection | null>,
    quality: AsyncIterator<QualityMetrics, QualityMetrics, QualityMetrics>,
  ) {
    this._imageInput = imageInput
    this._faceInput = faceInput
    this._quality = quality
  }

  start(): void {
    this._clearBestFrame()
    this._running = true
    this._frameCount = 0
    if (!__PROD__) console.log("[BFS] Started")
  }

  stop(): void {
    this._running = false
    const bestBlur = this._bestFrame?.quality?.blurScore || 0
    if (this._frameCount > 0)
      if (!__PROD__) console.log(`[BFS] Stopped: best blur=${(bestBlur * 1000000).toFixed(2)}`)
  }

  async send(frame: ImageSource): Promise<void> {
    if (!this._running) return

    this._imageInput.write(frame)
    this._faceInput.write(null)

    const qualityResult = await this._quality.next()
    if (!qualityResult.value) return

    this._frameCount++
    const currentBlurScore = qualityResult.value.blurScore
    const bestBlurScore = this._bestFrame?.quality?.blurScore || 0

    if (!this._bestFrame || currentBlurScore > bestBlurScore) {
      const bitmap = await createImageBitmap(frame)
      try {
        const canvasCopy = createCanvas(bitmap.width, bitmap.height)
        drawImage(canvasCopy, bitmap)
        this._replaceBestFrame({ image: canvasCopy, quality: qualityResult.value })
      } finally {
        bitmap.close()
      }
    }
  }

  async getBestFrame(): Promise<BestFrameResult | null> {
    return this._bestFrame
  }

  private _replaceBestFrame(result: BestFrameResult) {
    this._clearBestFrame()
    this._bestFrame = result
  }

  private _clearBestFrame() {
    if (this._bestFrame && isCanvasImageSource(this._bestFrame.image)) {
      destroyCanvas(this._bestFrame.image)
    }
    this._bestFrame = null
  }
}

export const createBestFrameSelector = async (): Promise<BestFrameSelector> => {
  const qualityGraph = await createQualityOnlineGraph()
  const imageInput = qualityGraph.in(0)
  const faceInput = qualityGraph.in(1)
  const quality = qualityGraph.out(0).data()

  return new BestFrameSelector(imageInput, faceInput, quality)
}
